import {
  OUTSTATION_GST_PERCENT_DECIMALS,
  OUTSTATION_GST_PERCENT_DEFAULT,
  OUTSTATION_GST_PERCENT_MAX,
  OUTSTATION_GST_PERCENT_MIN,
  OUTSTATION_KM_SLAB_MAX,
  OUTSTATION_KM_SLAB_MIN,
  OUTSTATION_KM_SLAB_UNLIMITED,
  OUTSTATION_SURGE_MULTIPLIER_DEFAULT,
  ZONE_PRICE_DECIMALS,
  ZONE_PRICE_MAX,
  ZONE_SURGE_MULTIPLIER_DECIMALS,
  ZONE_SURGE_MULTIPLIER_MAX,
  ZONE_SURGE_MULTIPLIER_MIN,
} from '../constants/transportPartnerFlow';
import { getCurrencySymbol, normalizeCurrencyCode } from './currency';

export interface OutstationPricingMasterRow {
  _id: string;
  code: string;
  name: string;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
}

export interface OutstationVehiclePrice {
  vehicleTypeCode: string;
  vehicleTypeName: string;
  baseFare: number;
  perKmRate: number;
  driverBataPerDay: number;
  nightCharge: number;
  waitingChargePerMinute: number;
  rowId?: string;
  rowCode?: string;
}

export interface OutstationPricingGroup {
  groupKey: string;
  pricingGroupCode: string;
  zoneCode: string;
  zoneName?: string;
  tripType: 'one_way' | 'round_trip';
  minimumKm: number;
  maximumKm: number;
  baseKm: number;
  gstPercentage: number;
  tollType: 'included' | 'actual';
  parkingType: 'included' | 'actual';
  surgeEnabled: boolean;
  surgeMultiplier: number;
  vehiclePrices: OutstationVehiclePrice[];
  isActive: boolean;
}

export function buildOutstationPricingGroupCode(
  zoneCode: string,
  tripType: string,
  minimumKm: number,
  maximumKm: number,
) {
  const max = maximumKm > 0 ? `${maximumKm}KM` : 'OPEN';
  return `${zoneCode}_${tripType.toUpperCase()}_${minimumKm}-${max}`.replace(/[^A-Za-z0-9_-]/g, '_');
}

export function formatOutstationTripType(tripType: string): string {
  return tripType === 'round_trip' ? 'Round Trip' : 'One Way';
}

export interface OutstationPricingDuplicateCheck {
  zoneCode: string;
  tripType: string;
  minimumKm: number;
  maximumKm: number;
  excludeGroupCode?: string;
  resolveZoneCode?: (raw: string | null | undefined) => string;
}

export function findDuplicateOutstationPricingGroup(
  groups: OutstationPricingGroup[],
  params: OutstationPricingDuplicateCheck,
): OutstationPricingGroup | undefined {
  const minKm = Number(params.minimumKm);
  const maxKm = Number(params.maximumKm);
  const tripType = params.tripType;

  return groups.find((group) => {
    if (params.excludeGroupCode && group.pricingGroupCode === params.excludeGroupCode) return false;
    if (!zoneCodesMatch(group.zoneCode, params.zoneCode, params.resolveZoneCode)) return false;
    if (group.tripType !== tripType) return false;
    if (Number(group.minimumKm) !== minKm) return false;
    if (Number(group.maximumKm) !== maxKm) return false;
    return true;
  });
}

export function getDuplicateOutstationPricingMessage(
  groups: OutstationPricingGroup[],
  params: OutstationPricingDuplicateCheck,
): string | undefined {
  const match = findDuplicateOutstationPricingGroup(groups, params);
  if (!match) return undefined;
  const slab = formatKmSlab(Number(params.minimumKm), Number(params.maximumKm));
  const trip = formatOutstationTripType(params.tripType);
  const zoneLabel = match.zoneName || params.zoneCode;
  return `Pricing for ${zoneLabel} · ${trip} · ${slab} already exists. Edit the existing slab or choose a different trip type / KM range.`;
}

/** True when two km slabs share at least one distance (closed intervals; max = 0 means unlimited). */
export function outstationKmSlabsOverlap(
  minimumKmA: number,
  maximumKmA: number,
  minimumKmB: number,
  maximumKmB: number,
): boolean {
  const minA = Number(minimumKmA);
  const minB = Number(minimumKmB);
  const endA = maximumKmA > OUTSTATION_KM_SLAB_UNLIMITED ? Number(maximumKmA) : Number.POSITIVE_INFINITY;
  const endB = maximumKmB > OUTSTATION_KM_SLAB_UNLIMITED ? Number(maximumKmB) : Number.POSITIVE_INFINITY;
  return minA <= endB && minB <= endA;
}

export function findOverlappingOutstationPricingGroup(
  groups: OutstationPricingGroup[],
  params: OutstationPricingDuplicateCheck,
): OutstationPricingGroup | undefined {
  const minKm = Number(params.minimumKm);
  const maxKm = Number(params.maximumKm);
  const tripType = params.tripType;

  return groups.find((group) => {
    if (params.excludeGroupCode && group.pricingGroupCode === params.excludeGroupCode) return false;
    if (!zoneCodesMatch(group.zoneCode, params.zoneCode, params.resolveZoneCode)) return false;
    if (group.tripType !== tripType) return false;
    if (Number(group.minimumKm) === minKm && Number(group.maximumKm) === maxKm) return false;
    return outstationKmSlabsOverlap(minKm, maxKm, group.minimumKm, group.maximumKm);
  });
}

export function getOverlappingOutstationPricingMessage(
  groups: OutstationPricingGroup[],
  params: OutstationPricingDuplicateCheck,
): string | undefined {
  const match = findOverlappingOutstationPricingGroup(groups, params);
  if (!match) return undefined;
  const newSlab = formatKmSlab(Number(params.minimumKm), Number(params.maximumKm));
  const existingSlab = formatKmSlab(match.minimumKm, match.maximumKm);
  const trip = formatOutstationTripType(params.tripType);
  const zoneLabel = match.zoneName || params.zoneCode;
  return `KM slab ${newSlab} overlaps existing slab ${existingSlab} for ${zoneLabel} · ${trip}. Choose a non-overlapping range (e.g. 1–100 and 101–150).`;
}

export function formatKmSlab(minimumKm: number, maximumKm: number) {
  if (minimumKm > 0 && maximumKm > 0) return `${minimumKm}–${maximumKm} km`;
  if (minimumKm > 0 && maximumKm === OUTSTATION_KM_SLAB_UNLIMITED) {
    return `${minimumKm}+ km (unlimited)`;
  }
  if (minimumKm > 0) return `${minimumKm}+ km`;
  if (maximumKm > 0) return `0–${maximumKm} km`;
  return 'All distances';
}

export type OutstationChargeType = 'included' | 'actual';

export function formatOutstationChargeType(type: OutstationChargeType): string {
  return type === 'actual' ? 'Excluded' : 'Included';
}

export function formatOutstationChargeTypeFieldLabel(
  label: string,
  type: OutstationChargeType,
): string {
  return type === 'actual'
    ? `${label}: Excluded (actual cost)`
    : `${label}: Included in fare`;
}

export function parseOutstationChargeType(raw: unknown): OutstationChargeType {
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'actual' || value === 'excluded' || value === 'exclude' || value === 'extra') {
    return 'actual';
  }
  return 'included';
}

export function outstationChargeTypeHelper(type: OutstationChargeType, label: string): string {
  return type === 'actual'
    ? `${label} excluded — actual cost added on top of the quoted fare`
    : `${label} included in the quoted fare`;
}

export function isOutstationChargeIncluded(type: OutstationChargeType): boolean {
  return type === 'included';
}

/** Canonical toll/parking metadata for admin save, mobile quote API, and billing. */
export function buildOutstationTollParkingMetadata(
  tollType: OutstationChargeType,
  parkingType: OutstationChargeType,
) {
  return {
    tollType,
    parkingType,
    tollIncluded: tollType === 'included',
    parkingIncluded: parkingType === 'included',
    /** Legacy aliases read by older rows / mobile clients */
    toll: tollType,
    parking: parkingType,
  };
}

export function validateOutstationGstPercentage(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Enter a valid GST percentage.';
  }

  const n = Number(value);
  if (n < OUTSTATION_GST_PERCENT_MIN) return 'GST cannot be negative.';
  if (n > OUTSTATION_GST_PERCENT_MAX) {
    return `GST cannot exceed ${OUTSTATION_GST_PERCENT_MAX}%.`;
  }

  const factor = 10 ** OUTSTATION_GST_PERCENT_DECIMALS;
  if (Math.abs(n * factor - Math.round(n * factor)) > 1e-6) {
    return `Use at most ${OUTSTATION_GST_PERCENT_DECIMALS} decimal places.`;
  }

  return undefined;
}

export function normalizeOutstationGstPercentage(value: number | string | null | undefined): number {
  const error = validateOutstationGstPercentage(value);
  if (error) return OUTSTATION_GST_PERCENT_DEFAULT;
  return Number(value);
}

export function validateOutstationMinimumKm(
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Min KM is required.';
  }
  const n = Number(value);
  if (n < 0) return 'Min KM cannot be negative.';
  if (!Number.isInteger(n)) return 'Min KM must be a whole number.';
  if (n < OUTSTATION_KM_SLAB_MIN) {
    return `Min KM must be at least ${OUTSTATION_KM_SLAB_MIN}.`;
  }
  if (n > OUTSTATION_KM_SLAB_MAX) {
    return `Min KM cannot exceed ${OUTSTATION_KM_SLAB_MAX.toLocaleString('en-IN')}.`;
  }
  return undefined;
}

export function validateOutstationMaximumKm(
  minimumKm: number,
  value: number | string | null | undefined,
): string | undefined {
  if (value === '' || value == null) return undefined;
  if (!Number.isFinite(Number(value))) return 'Enter a valid Max KM.';
  const n = Number(value);
  if (n === OUTSTATION_KM_SLAB_UNLIMITED) return undefined;
  if (n < 0) return 'Max KM cannot be negative.';
  if (!Number.isInteger(n)) return 'Max KM must be a whole number.';
  if (minimumKm >= OUTSTATION_KM_SLAB_MIN && n < minimumKm) {
    return 'Max KM must be greater than or equal to Min KM.';
  }
  if (n > OUTSTATION_KM_SLAB_MAX) {
    return `Max KM cannot exceed ${OUTSTATION_KM_SLAB_MAX.toLocaleString('en-IN')}.`;
  }
  return undefined;
}

export function normalizeOutstationMaximumKm(value: number | string | null | undefined): number {
  if (value === '' || value == null) return OUTSTATION_KM_SLAB_UNLIMITED;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= OUTSTATION_KM_SLAB_UNLIMITED) return OUTSTATION_KM_SLAB_UNLIMITED;
  return n;
}

export function normalizeOutstationMinimumKm(value: number | string | null | undefined): number {
  const error = validateOutstationMinimumKm(value);
  if (error) return OUTSTATION_KM_SLAB_MIN;
  return Number(value);
}

export function validateOutstationKmSlab(
  minimumKm: number | string | null | undefined,
  maximumKm: number | string | null | undefined,
): string | undefined {
  const minError = validateOutstationMinimumKm(minimumKm);
  if (minError) return minError;
  return validateOutstationMaximumKm(Number(minimumKm), maximumKm);
}

export function validateOutstationBaseKm(
  value: number | string | null | undefined,
  options?: { minimumKm?: number; maximumKm?: number },
): string | undefined {
  if (value === '' || value == null) return undefined;
  if (!Number.isFinite(Number(value))) return 'Enter a valid Base KM.';
  const n = Number(value);
  if (n === 0) return undefined;
  if (n < 0) return 'Base KM cannot be negative.';
  if (!Number.isInteger(n)) return 'Base KM must be a whole number.';
  if (n > OUTSTATION_KM_SLAB_MAX) {
    return `Base KM cannot exceed ${OUTSTATION_KM_SLAB_MAX.toLocaleString('en-IN')}.`;
  }

  const minKm = options?.minimumKm ?? 0;
  const maxKm = options?.maximumKm ?? OUTSTATION_KM_SLAB_UNLIMITED;

  if (maxKm > OUTSTATION_KM_SLAB_UNLIMITED) {
    const slabMin = minKm >= OUTSTATION_KM_SLAB_MIN ? minKm : OUTSTATION_KM_SLAB_MIN;
    if (n < slabMin || n > maxKm) {
      return `Base KM must be between ${slabMin} and ${maxKm} km for this slab (or 0 for none).`;
    }
    return undefined;
  }

  if (minKm >= OUTSTATION_KM_SLAB_MIN && n < minKm) {
    return `Base KM must be at least ${minKm} km (slab Min KM), or 0 for none.`;
  }

  return undefined;
}

export function outstationBaseKmHelper(options?: { minimumKm?: number; maximumKm?: number }): string {
  const minKm = options?.minimumKm ?? 0;
  const maxKm = options?.maximumKm ?? OUTSTATION_KM_SLAB_UNLIMITED;
  if (maxKm > OUTSTATION_KM_SLAB_UNLIMITED) {
    const slabMin = minKm >= OUTSTATION_KM_SLAB_MIN ? minKm : OUTSTATION_KM_SLAB_MIN;
    return `No per-km charge within base · use ${slabMin}–${maxKm} km or 0 for none`;
  }
  if (minKm >= OUTSTATION_KM_SLAB_MIN) {
    return `No per-km charge within base · use ≥ ${minKm} km or 0 for none`;
  }
  return 'No per-km charge within this distance · 0 = none included';
}

export function validateOutstationSurgeMultiplier(
  value: number | string | null | undefined,
  options?: { required?: boolean },
): string | undefined {
  if (!options?.required) {
    if (value === '' || value == null) return undefined;
  } else if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return 'Enter a surge multiplier when surge is enabled.';
  }

  const n = Number(value);
  if (!Number.isFinite(n)) return 'Enter a valid surge multiplier.';
  if (n <= ZONE_SURGE_MULTIPLIER_MIN) {
    return `Surge multiplier must be greater than ${ZONE_SURGE_MULTIPLIER_MIN} when surge is enabled.`;
  }
  if (n > ZONE_SURGE_MULTIPLIER_MAX) {
    return `Surge multiplier cannot exceed ${ZONE_SURGE_MULTIPLIER_MAX}.`;
  }

  const factor = 10 ** ZONE_SURGE_MULTIPLIER_DECIMALS;
  if (Math.abs(n * factor - Math.round(n * factor)) > 1e-6) {
    return `Use at most ${ZONE_SURGE_MULTIPLIER_DECIMALS} decimal places.`;
  }

  return undefined;
}

export function normalizeOutstationSurgeMultiplier(
  value: number | string | null | undefined,
  surgeEnabled: boolean,
): number {
  if (!surgeEnabled) return ZONE_SURGE_MULTIPLIER_MIN;
  const error = validateOutstationSurgeMultiplier(value, { required: true });
  if (error) return OUTSTATION_SURGE_MULTIPLIER_DEFAULT;
  return Number(value);
}

export function normalizeOutstationBaseKm(
  value: number | string | null | undefined,
  options?: { minimumKm?: number; maximumKm?: number },
): number {
  if (value === '' || value == null) return OUTSTATION_KM_SLAB_UNLIMITED;
  const error = validateOutstationBaseKm(value, options);
  if (error) return OUTSTATION_KM_SLAB_UNLIMITED;
  return Number(value);
}

function validateOutstationMonetaryAmount(
  value: number | string | null | undefined,
  options: {
    required?: boolean;
    label: string;
    allowZero?: boolean;
  },
): string | undefined {
  const { required, label, allowZero = true } = options;

  if (!required) {
    if (value === '' || value == null) return undefined;
  } else if (value === '' || value == null || !Number.isFinite(Number(value))) {
    return `Enter a valid ${label.toLowerCase()}.`;
  }

  const n = Number(value);
  if (!Number.isFinite(n)) return `Enter a valid ${label.toLowerCase()}.`;
  if (n < 0) return `${label} cannot be negative.`;
  if (!allowZero && n <= 0) return `${label} must be greater than 0.`;
  if (n > ZONE_PRICE_MAX) {
    return `${label} cannot exceed ${ZONE_PRICE_MAX.toLocaleString('en-IN')}.`;
  }

  const factor = 10 ** ZONE_PRICE_DECIMALS;
  if (Math.abs(n * factor - Math.round(n * factor)) > 1e-6) {
    return `Use at most ${ZONE_PRICE_DECIMALS} decimal places.`;
  }

  return undefined;
}

export function validateOutstationVehicleBaseFare(
  value: number | string | null | undefined,
  options?: { required?: boolean },
): string | undefined {
  return validateOutstationMonetaryAmount(value, {
    required: options?.required,
    label: 'Base fare',
    allowZero: false,
  });
}

export function validateOutstationVehiclePerKmRate(
  value: number | string | null | undefined,
  options?: { required?: boolean },
): string | undefined {
  return validateOutstationMonetaryAmount(value, {
    required: options?.required,
    label: 'Per km rate',
    allowZero: false,
  });
}

export function validateOutstationVehicleBata(
  value: number | string | null | undefined,
): string | undefined {
  return validateOutstationMonetaryAmount(value, {
    label: 'BATA',
    allowZero: true,
  });
}

export function validateOutstationVehicleNightCharge(
  value: number | string | null | undefined,
): string | undefined {
  return validateOutstationMonetaryAmount(value, {
    label: 'Night charge',
    allowZero: true,
  });
}

export function validateOutstationVehicleWaitingCharge(
  value: number | string | null | undefined,
): string | undefined {
  return validateOutstationMonetaryAmount(value, {
    label: 'Wait/min',
    allowZero: true,
  });
}

export function normalizeOutstationVehicleBaseFare(
  value: number | string | null | undefined,
): number {
  const error = validateOutstationVehicleBaseFare(value, { required: true });
  if (error) return 0;
  return Number(value);
}

export function normalizeOutstationVehiclePerKmRate(
  value: number | string | null | undefined,
): number {
  const error = validateOutstationVehiclePerKmRate(value, { required: true });
  if (error) return 0;
  return Number(value);
}

export function normalizeOutstationVehicleBata(
  value: number | string | null | undefined,
): number {
  if (value === '' || value == null) return 0;
  const error = validateOutstationVehicleBata(value);
  if (error) return 0;
  return Number(value);
}

export function normalizeOutstationVehicleNightCharge(
  value: number | string | null | undefined,
): number {
  if (value === '' || value == null) return 0;
  const error = validateOutstationVehicleNightCharge(value);
  if (error) return 0;
  return Number(value);
}

export function normalizeOutstationVehicleWaitingCharge(
  value: number | string | null | undefined,
): number {
  if (value === '' || value == null) return 0;
  const error = validateOutstationVehicleWaitingCharge(value);
  if (error) return 0;
  return Number(value);
}

export function validateOutstationVehiclePricing(
  vehiclePrices: Array<Pick<OutstationVehiclePrice, 'vehicleTypeCode' | 'vehicleTypeName' | 'baseFare' | 'perKmRate' | 'driverBataPerDay' | 'nightCharge' | 'waitingChargePerMinute'>>,
  options?: { expectedVehicleCount?: number },
): string | undefined {
  if (!vehiclePrices.length) {
    return 'Set base fare and per km rate for all vehicle types.';
  }

  if (options?.expectedVehicleCount != null && vehiclePrices.length < options.expectedVehicleCount) {
    return 'Set base fare and per km rate for all vehicle types.';
  }

  for (const vp of vehiclePrices) {
    const label = vp.vehicleTypeName || vp.vehicleTypeCode;
    const baseErr = validateOutstationVehicleBaseFare(vp.baseFare, { required: true });
    if (baseErr) return `${label}: ${baseErr}`;
    const perKmErr = validateOutstationVehiclePerKmRate(vp.perKmRate, { required: true });
    if (perKmErr) return `${label}: ${perKmErr}`;
    const bataErr = validateOutstationVehicleBata(vp.driverBataPerDay);
    if (bataErr) return `${label}: ${bataErr}`;
    const nightErr = validateOutstationVehicleNightCharge(vp.nightCharge);
    if (nightErr) return `${label}: ${nightErr}`;
    const waitingErr = validateOutstationVehicleWaitingCharge(vp.waitingChargePerMinute);
    if (waitingErr) return `${label}: ${waitingErr}`;
  }

  return undefined;
}

function roundOutstationMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Compact amount for fare formula text (e.g. $100, ₹85). */
export function formatOutstationInlineAmount(amount: number, currency?: string | null): string {
  const mark = getCurrencySymbol(currency) || normalizeCurrencyCode(currency);
  return `${mark}${amount}`;
}

export interface OutstationFareInput {
  totalKm: number;
  baseKm: number;
  baseFare: number;
  perKmRate: number;
  driverBataPerDay?: number;
  nightCharge?: number;
  waitingMinutes?: number;
  waitingChargePerMinute?: number;
  gstPercentage?: number;
  surgeEnabled?: boolean;
  surgeMultiplier?: number;
  tollType?: OutstationChargeType;
  parkingType?: OutstationChargeType;
  /** Zone ISO currency — drives formula labels (USD, INR, etc.). */
  currency?: string | null;
}

export interface OutstationFareBreakdownLine {
  id: string;
  label: string;
  formula: string;
  amount: number;
}

export interface OutstationFareBreakdown {
  totalKm: number;
  baseKmIncluded: number;
  chargeableKm: number;
  lines: OutstationFareBreakdownLine[];
  subtotal: number;
  surgeAmount: number;
  gst: number;
  total: number;
  tollType: OutstationChargeType;
  parkingType: OutstationChargeType;
  tollIncluded: boolean;
  parkingIncluded: boolean;
  /** Vehicle fare total — toll/parking excluded items are added separately on the final bill. */
  quotedVehicleTotal: number;
}

function appendOutstationTollParkingPolicyLines(
  lines: OutstationFareBreakdownLine[],
  tollType: OutstationChargeType,
  parkingType: OutstationChargeType,
): OutstationFareBreakdownLine[] {
  return [
    ...lines,
    {
      id: 'toll_policy',
      label: 'Toll',
      formula: tollType === 'included' ? 'Included in quoted fare' : 'Excluded — billed at actual cost on final bill',
      amount: 0,
    },
    {
      id: 'parking_policy',
      label: 'Parking',
      formula: parkingType === 'included' ? 'Included in quoted fare' : 'Excluded — billed at actual cost on final bill',
      amount: 0,
    },
  ];
}

/** Base KM is included — per-km rate applies only to distance beyond base KM. */
export function calculateOutstationFare(input: OutstationFareInput): OutstationFareBreakdown {
  const tollType = input.tollType ?? 'included';
  const parkingType = input.parkingType ?? 'included';
  const money = (amount: number) => formatOutstationInlineAmount(amount, input.currency);
  const totalKm = Math.max(0, input.totalKm);
  const baseKmIncluded = Math.max(0, input.baseKm ?? 0);
  const chargeableKm = Math.max(0, totalKm - baseKmIncluded);
  const baseFare = input.baseFare ?? 0;
  const perKmRate = input.perKmRate ?? 0;
  const distanceCharge = roundOutstationMoney(chargeableKm * perKmRate);
  const driverBata = input.driverBataPerDay ?? 0;
  const nightCharge = input.nightCharge ?? 0;
  const waitingMinutes = Math.max(0, input.waitingMinutes ?? 0);
  const waitingCharge = roundOutstationMoney(waitingMinutes * (input.waitingChargePerMinute ?? 0));
  const lines: OutstationFareBreakdownLine[] = [];

  if (baseFare > 0) {
    lines.push({
      id: 'base',
      label: 'Base amount',
      formula: money(baseFare),
      amount: baseFare,
    });
  }

  if (chargeableKm > 0 && perKmRate > 0) {
    lines.push({
      id: 'distance',
      label: 'Distance charge',
      formula: `(${totalKm} − ${baseKmIncluded}) km × ${money(perKmRate)}`,
      amount: distanceCharge,
    });
  } else {
    lines.push({
      id: 'distance',
      label: 'Distance charge',
      formula: baseKmIncluded > 0
        ? `${totalKm} km within ${baseKmIncluded} km base — no per-km charge`
        : 'No distance charge',
      amount: 0,
    });
  }

  if (driverBata > 0) {
    lines.push({ id: 'bata', label: 'Driver bata', formula: money(driverBata), amount: driverBata });
  }
  if (nightCharge > 0) {
    lines.push({ id: 'night', label: 'Night charge', formula: money(nightCharge), amount: nightCharge });
  }
  if (waitingCharge > 0) {
    lines.push({
      id: 'waiting',
      label: 'Waiting charge',
      formula: `${waitingMinutes} min × ${money(input.waitingChargePerMinute ?? 0)}`,
      amount: waitingCharge,
    });
  }

  const subtotalBeforeSurge = roundOutstationMoney(baseFare + distanceCharge + driverBata + nightCharge + waitingCharge);
  let surgeAmount = 0;
  const applySurge = input.surgeEnabled === true
    && (input.surgeMultiplier ?? ZONE_SURGE_MULTIPLIER_MIN) > ZONE_SURGE_MULTIPLIER_MIN;
  if (applySurge) {
    const multiplier = input.surgeMultiplier ?? ZONE_SURGE_MULTIPLIER_MIN;
    const surgedSubtotal = roundOutstationMoney(subtotalBeforeSurge * multiplier);
    surgeAmount = roundOutstationMoney(surgedSubtotal - subtotalBeforeSurge);
    lines.push({
      id: 'surge',
      label: 'Surge multiplier',
      formula: `× ${multiplier}`,
      amount: surgeAmount,
    });
  }
  const subtotal = roundOutstationMoney(subtotalBeforeSurge + surgeAmount);
  const gstRate = (input.gstPercentage ?? OUTSTATION_GST_PERCENT_DEFAULT) / 100;
  const gst = roundOutstationMoney(subtotal * gstRate);
  const total = roundOutstationMoney(subtotal + gst);

  return {
    totalKm,
    baseKmIncluded,
    chargeableKm,
    lines: appendOutstationTollParkingPolicyLines(lines, tollType, parkingType),
    subtotal,
    surgeAmount,
    gst,
    total,
    tollType,
    parkingType,
    tollIncluded: isOutstationChargeIncluded(tollType),
    parkingIncluded: isOutstationChargeIncluded(parkingType),
    quotedVehicleTotal: total,
  };
}

/** Map zone metadata aliases (code, name, id) to the canonical zone code from Zone Management. */
export function buildZoneCodeResolver(
  zones: Array<{ _id: string; code: string; name?: string }>,
) {
  const aliasToCanonical = new Map<string, string>();

  const register = (alias: string, canonical: string) => {
    const trimmed = alias.trim();
    if (!trimmed) return;
    aliasToCanonical.set(trimmed, canonical);
    aliasToCanonical.set(trimmed.toUpperCase(), canonical);
  };

  for (const zone of zones) {
    const canonical = zone.code.trim();
    if (!canonical) continue;
    register(canonical, canonical);
    register(zone._id, canonical);
    if (zone.name) register(zone.name, canonical);
  }

  return (raw: string | null | undefined): string => {
    if (raw == null) return '';
    const trimmed = String(raw).trim();
    if (!trimmed) return '';
    return aliasToCanonical.get(trimmed) || aliasToCanonical.get(trimmed.toUpperCase()) || trimmed;
  };
}

export function zoneCodesMatch(
  a: string | null | undefined,
  b: string | null | undefined,
  resolveZoneCode?: (raw: string | null | undefined) => string,
): boolean {
  const resolve = resolveZoneCode || ((raw) => String(raw ?? '').trim());
  const left = resolve(a);
  const right = resolve(b);
  if (!left || !right) return false;
  return left.toUpperCase() === right.toUpperCase();
}

export type OutstationZoneRow = {
  _id: string;
  code: string;
  name?: string;
  zoneLevel?: string;
  primaryZoneId?: string;
  currency?: string;
};

export function resolveOutstationZoneCurrency(
  rawCode: string | null | undefined,
  zones: OutstationZoneRow[],
  resolveZoneCode: (raw: string | null | undefined) => string,
): string {
  const pricingCode = resolveOutstationPricingZoneCode(rawCode, zones, resolveZoneCode);
  const zone = findZoneByCode(zones, pricingCode, resolveZoneCode)
    || findZoneByCode(zones, rawCode, resolveZoneCode);
  return normalizeCurrencyCode(zone?.currency);
}

export function findZoneByCode(
  zones: OutstationZoneRow[],
  rawCode: string | null | undefined,
  resolveZoneCode: (raw: string | null | undefined) => string,
): OutstationZoneRow | undefined {
  return zones.find((z) => zoneCodesMatch(z.code, rawCode, resolveZoneCode));
}

/** Outstation pricing is owned by primary zones; secondary zones inherit from their primary. */
export function resolveOutstationPricingZoneCode(
  raw: string | null | undefined,
  zones: OutstationZoneRow[],
  resolveZoneCode: (raw: string | null | undefined) => string,
): string {
  const code = resolveZoneCode(raw);
  if (!code) return '';
  const zone = findZoneByCode(zones, code, resolveZoneCode);
  if (zone?.zoneLevel === 'SECONDARY' && zone.primaryZoneId) {
    const primary = zones.find((z) => z._id === zone.primaryZoneId);
    if (primary?.code) return resolveZoneCode(primary.code);
  }
  return code;
}

export function buildOutstationPricingZoneResolver(zones: OutstationZoneRow[]) {
  const resolveZoneCode = buildZoneCodeResolver(zones);
  return (raw: string | null | undefined) =>
    resolveOutstationPricingZoneCode(raw, zones, resolveZoneCode);
}

export function isSecondaryOutstationZone(
  zoneCode: string | null | undefined,
  zones: OutstationZoneRow[],
  resolveZoneCode: (raw: string | null | undefined) => string,
): boolean {
  const zone = findZoneByCode(zones, zoneCode, resolveZoneCode);
  return zone?.zoneLevel === 'SECONDARY';
}

export function getPrimaryZoneForCode(
  zoneCode: string | null | undefined,
  zones: OutstationZoneRow[],
  resolveZoneCode: (raw: string | null | undefined) => string,
): OutstationZoneRow | undefined {
  const zone = findZoneByCode(zones, zoneCode, resolveZoneCode);
  if (!zone) return undefined;
  if (zone.zoneLevel === 'SECONDARY' && zone.primaryZoneId) {
    return zones.find((z) => z._id === zone.primaryZoneId);
  }
  return zone.zoneLevel === 'PRIMARY' ? zone : undefined;
}

export function listPrimaryOutstationZones(zones: OutstationZoneRow[]) {
  return zones.filter((z) => z.zoneLevel === 'PRIMARY');
}

/** Row is active only when both isActive and metadata.status agree (when status is set). */
export function isOutstationPricingRowActive(row: OutstationPricingMasterRow): boolean {
  if (row.isActive === false) return false;
  const status = String(row.metadata?.status || '').trim().toLowerCase();
  if (status === 'inactive' || status === 'disabled') return false;
  return true;
}

export function patchOutstationPricingRows(
  rows: OutstationPricingMasterRow[],
  group: OutstationPricingGroup,
  isActive: boolean,
): OutstationPricingMasterRow[] {
  const rowIds = new Set(group.vehiclePrices.map((vp) => vp.rowId).filter(Boolean));
  const status = isActive ? 'active' : 'inactive';
  return rows.map((row) => {
    if (!rowIds.has(row._id)) return row;
    return {
      ...row,
      isActive,
      metadata: { ...row.metadata, status },
    };
  });
}

export function groupOutstationPricing(
  rows: OutstationPricingMasterRow[],
  zoneNameMap: Map<string, string>,
  vehicleNameMap: Map<string, string>,
  resolveZoneCode?: (raw: string | null | undefined) => string,
): OutstationPricingGroup[] {
  const map = new Map<string, OutstationPricingGroup>();

  for (const row of rows) {
    const meta = row.metadata || {};
    const zoneCode = resolveZoneCode
      ? resolveZoneCode(String(meta.zoneCode || ''))
      : String(meta.zoneCode || '');
    const tripType = meta.tripType === 'round_trip' ? 'round_trip' : 'one_way';
    const minimumKm = Number(meta.minimumKm || 0);
    const maximumKm = Number(meta.maximumKm || 0);
    const pricingGroupCode = String(
      meta.pricingGroupCode || buildOutstationPricingGroupCode(zoneCode, tripType, minimumKm, maximumKm),
    );
    const groupKey = `${zoneCode}::${pricingGroupCode}`;

    let group = map.get(groupKey);
    if (!group) {
      group = {
        groupKey,
        pricingGroupCode,
        zoneCode,
        zoneName: zoneNameMap.get(zoneCode) || zoneCode,
        tripType,
        minimumKm,
        maximumKm,
        baseKm: Number(meta.baseKm || 0),
        gstPercentage: Number(meta.gstPercentage ?? 5),
        tollType: parseOutstationChargeType(meta.tollType ?? meta.toll),
        parkingType: parseOutstationChargeType(meta.parkingType ?? meta.parking),
        surgeEnabled: meta.surgeEnabled === true || meta.surgeEnabled === 'true',
        surgeMultiplier: Number(meta.surgeMultiplier ?? ZONE_SURGE_MULTIPLIER_MIN) || ZONE_SURGE_MULTIPLIER_MIN,
        vehiclePrices: [],
        isActive: isOutstationPricingRowActive(row),
      };
      map.set(groupKey, group);
    }

    const vehicleTypeCode = String(meta.vehicleTypeCode || '');
    group.vehiclePrices.push({
      vehicleTypeCode,
      vehicleTypeName: vehicleNameMap.get(vehicleTypeCode) || vehicleTypeCode,
      baseFare: Number(meta.baseFare || 0),
      perKmRate: Number(meta.perKmRate || 0),
      driverBataPerDay: Number(meta.driverBataPerDay || 0),
      nightCharge: Number(meta.nightCharge || 0),
      waitingChargePerMinute: Number(meta.waitingChargePerMinute || 0),
      rowId: row._id,
      rowCode: row.code,
    });
    if (!isOutstationPricingRowActive(row)) group.isActive = false;
  }

  return Array.from(map.values()).sort((a, b) => {
    if (a.zoneCode !== b.zoneCode) return a.zoneCode.localeCompare(b.zoneCode);
    if (a.tripType !== b.tripType) return a.tripType.localeCompare(b.tripType);
    if (a.minimumKm !== b.minimumKm) return a.minimumKm - b.minimumKm;
    return a.maximumKm - b.maximumKm;
  });
}

/** Count grouped outstation pricing packages per primary zone (not per vehicle row). */
export function countOutstationPricingGroupsByZone(
  rows: OutstationPricingMasterRow[],
  resolveZoneCode: (raw: string | null | undefined) => string = (raw) => String(raw ?? '').trim(),
): Map<string, { total: number; active: number }> {
  const groups = groupOutstationPricing(rows, new Map(), new Map(), resolveZoneCode);
  const counts = new Map<string, { total: number; active: number }>();
  for (const group of groups) {
    const zoneCode = resolveZoneCode(group.zoneCode);
    if (!zoneCode) continue;
    const entry = counts.get(zoneCode) || { total: 0, active: 0 };
    entry.total += 1;
    if (group.isActive) entry.active += 1;
    counts.set(zoneCode, entry);
  }
  return counts;
}

export function lookupOutstationPackageCounts(
  zoneCode: string,
  counts: Map<string, { total: number; active: number }>,
  resolvePricingZoneCode: (raw: string | null | undefined) => string,
) {
  const pricingCode = resolvePricingZoneCode(zoneCode);
  if (!pricingCode) return undefined;
  return counts.get(pricingCode) ?? counts.get(pricingCode.toUpperCase());
}
